import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { FileAttachments } from 'src/default/interfaces/communication/email-communication'

import { EmailSenderState, EmailView } from './emailSender.types'

import { IEmailTemplate } from '@v2/domains/email-template/models/emailTemplate.model'

const initialEmailSenderState: EmailSenderState = {
  from: { id: '', email: '' },
  replyTo: { id: '', email: '' },
  subject: '',
  body: '',
  domainSignature: '',
  attachments: [],
  filesAttachments: [],
  isUploadingAttachments: false,
  view: 'create',
  templateModal: {
    showManager: false,
    showList: true,
    showNew: false,
    selected: undefined,
  },
}

const emailSenderSlice = createSlice({
  name: 'emailSender',
  initialState: initialEmailSenderState,
  reducers: {
    setView: (state, action: PayloadAction<EmailView>) => {
      state.view = action.payload
    },

    setFrom: (state, action: PayloadAction<{ id: string; email: string }>) => {
      state.from = action.payload
    },

    setReplyTo: (
      state,
      action: PayloadAction<{ id: string; email: string }>,
    ) => {
      state.replyTo = action.payload
    },

    setSubject: (state, action: PayloadAction<string>) => {
      state.subject = action.payload
    },

    setBody: (state, action: PayloadAction<string>) => {
      state.body = action.payload
    },

    setDomainSignature: (state, action: PayloadAction<string>) => {
      state.domainSignature = action.payload
    },

    setIsUploadingAttachments: (state, action: PayloadAction<boolean>) => {
      state.isUploadingAttachments = action.payload
    },

    addAttachments: (
      state,
      action: PayloadAction<{
        files: File[]
        metadata: FileAttachments[]
      }>,
    ) => {
      state.attachments = [...action.payload.files, ...state.attachments]
      state.filesAttachments = [
        ...action.payload.metadata,
        ...state.filesAttachments,
      ]
    },

    removeAttachment: (state, action: PayloadAction<number>) => {
      const index = action.payload
      state.attachments.splice(index, 1)
      state.filesAttachments.splice(index, 1)
    },

    clearAttachments: (state) => {
      state.attachments = []
      state.filesAttachments = []
    },

    openTemplateManager: (state) => {
      state.templateModal.showManager = true
      state.templateModal.showList = true
      state.templateModal.showNew = false
    },

    closeTemplateManager: (state) => {
      state.templateModal.showManager = false
      state.templateModal.showList = true
      state.templateModal.showNew = false
      state.templateModal.selected = undefined
    },

    showNewTemplateModal: (
      state,
      action: PayloadAction<IEmailTemplate | undefined>,
    ) => {
      state.templateModal.showList = false
      state.templateModal.showNew = true
      state.templateModal.selected = action.payload
    },

    showListTemplateModal: (state) => {
      state.templateModal.showList = true
      state.templateModal.showNew = false
      state.templateModal.selected = undefined
    },

    clearTemplateSelected: (state) => {
      state.templateModal.selected = undefined
    },

    loadEmailForView: (
      state,
      action: PayloadAction<{
        from: { id: string; email: string }
        replyTo: { id: string; email: string }
        subject: string
        body: string
        filesAttachments: FileAttachments[]
      }>,
    ) => {
      state.from = action.payload.from
      state.replyTo = action.payload.replyTo
      state.subject = action.payload.subject
      state.body = action.payload.body
      state.filesAttachments = action.payload.filesAttachments
      state.view = 'view'
    },

    resetEmailSender: () => {
      return initialEmailSenderState
    },
  },
})

export const {
  setView,
  setFrom,
  setReplyTo,
  setSubject,
  setBody,
  setDomainSignature,
  setIsUploadingAttachments,
  addAttachments,
  removeAttachment,
  clearAttachments,
  openTemplateManager,
  closeTemplateManager,
  showNewTemplateModal,
  showListTemplateModal,
  clearTemplateSelected,
  loadEmailForView,
  resetEmailSender,
} = emailSenderSlice.actions

export default emailSenderSlice.reducer
